import Uppy, { UppyOptions } from '@uppy/core';
import DropTarget from '@uppy/drop-target';
import {
  ChangeEventHandler,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Platform, SubmissionType, UploadOperation } from 'src/types';
import { trackFileUploadUpdated } from 'src/utils/analytics';
import {
  formatValidationError,
  getFileExtension,
  validateFileUpload,
} from 'src/utils/validateFileUpload/fileValidation';
import { createUppy } from './helpers';
import { readableErrorMessage } from './helpers/readableErrorMessage';

interface FileUploadProviderProps {
  children: ReactNode;
  id: string;
  organisationId?: string;
  segmentEnabled: boolean;
  submissionType: SubmissionType;
  platform: Platform;
}

const defaultState: {
  uppy: Uppy | null;
  setLastUpdated: (lastUpdated: number) => void;
  error: string | null;
  setError: (error: string | null) => void;
  uploadErrorCount: number;
  setUploadErrorCount: (count: number) => void;
  isUploadInFlight?: boolean;
  setIsUploadInFlight?: (isUploadInFlight: boolean) => void;
  failedFiles: string[];
  setFailedFiles: (files: string[]) => void;
} = {
  uppy: null,
  setLastUpdated: () => {},
  error: null,
  setError: () => {},
  uploadErrorCount: 0,
  setUploadErrorCount: () => {},
  failedFiles: [],
  setFailedFiles: () => {},
};

const FileUploadContext = createContext(defaultState);

export const FileUploadProvider = ({
  children,
  id,
  organisationId,
  segmentEnabled,
  submissionType,
  platform,
}: FileUploadProviderProps) => {
  const [uppy, setUppy] = useState<Uppy | null>(null);
  const [, setLastUpdated] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [uploadErrorCount, setUploadErrorCount] = useState(0);
  const [isUploadInFlight, setIsUploadInFlight] = useState(false);
  const [failedFiles, setFailedFiles] = useState<string[]>([]);

  if (!uppy) {
    const newUppy = createUppy({ id });
    setUppy(newUppy);

    newUppy.on('thumbnail:generated', () => {
      setLastUpdated(Date.now());
    });

    newUppy.on('restriction-failed', (file, error) => {
      const errorMessage = (error as Error).message;

      if (errorMessage.includes('is not a supported format')) {
        const filename = file?.name || 'Unknown file';
        setFailedFiles((prev) => {
          const updated = [...prev, filename];
          setError(formatValidationError(updated));
          return updated;
        });
      } else {
        setError(errorMessage);
      }
    });

    newUppy.on('upload-error', (file) => {
      if (file) {
        newUppy.removeFile(file.id);
      }
    });
  }

  useEffect(() => {
    if (segmentEnabled) {
      uppy?.on('file-added', (file) => {
        const properties = {
          organisation_id: organisationId,
          file_type: file.type as string,
          file_size: file.size as number,
          operation: UploadOperation.ADDED,
          submissionType,
          platform,
        };

        trackFileUploadUpdated(properties);
      });

      uppy?.on('restriction-failed', (file, error) => {
        if (file) {
          const properties = {
            organisation_id: organisationId,
            file_type: file.type as string,
            file_size: file.size as number,
            error: readableErrorMessage(error.message),
            operation: UploadOperation.ADDED,
            submissionType,
            platform,
          };

          trackFileUploadUpdated(properties);
        }
      });
      uppy?.on('file-removed', (file, reason) => {
        if (reason === 'removed-by-user') {
          const properties = {
            organisation_id: organisationId,
            file_type: file.type as string,
            file_size: file.size as number,
            operation: UploadOperation.REMOVED,
            submissionType,
            platform,
          };

          trackFileUploadUpdated(properties);
        }
      });
    }
  }, [uppy, segmentEnabled, organisationId, submissionType, platform]);

  return (
    <FileUploadContext.Provider
      value={{
        uppy,
        setLastUpdated,
        error,
        setError,
        uploadErrorCount,
        setUploadErrorCount,
        isUploadInFlight,
        setIsUploadInFlight,
        failedFiles,
        setFailedFiles,
      }}
    >
      {children}
    </FileUploadContext.Provider>
  );
};

export const useFileUploadContext = () => {
  const context = useContext(FileUploadContext);
  const {
    uppy,
    setError,
    setLastUpdated,
    uploadErrorCount,
    setUploadErrorCount,
    isUploadInFlight,
    setIsUploadInFlight,
    setFailedFiles,
  } = context;

  const handleFileSelect: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      setError(null);
      setFailedFiles([]);
      const files = e.target.files ?? [];

      const uppyFiles = [];
      const failed: string[] = [];

      for (const file of files) {
        try {
          validateFileUpload({ filename: file.name, mimeType: file.type });
          uppyFiles.push({
            name: file.name,
            type: file.type,
            data: file,
            source: 'Local',
            isRemote: false,
          });
        } catch (validationError) {
          failed.push(file.name);
        }
      }

      if (failed.length > 0) {
        setError(formatValidationError(failed));
        setFailedFiles(failed);
        setLastUpdated(Date.now());
        return;
      }

      try {
        uppy?.addFiles(uppyFiles);
      } catch (err) {
        const extension = files[0]?.name
          ? getFileExtension(files[0].name)
          : undefined;
        const errorMessage = readableErrorMessage(
          (err as Error).message,
          extension,
        );
        setError(errorMessage);
      }

      setLastUpdated(Date.now());
    },
    [uppy, setError, setLastUpdated, setFailedFiles],
  );

  const handleFileRemove = useCallback(
    (id: string) => {
      uppy?.removeFile(id);
      setLastUpdated(Date.now());
      setError(null);
      setFailedFiles([]);
    },
    [setError, setLastUpdated, setFailedFiles, uppy],
  );

  const initialiseDropArea = useCallback(
    (dropAreaId: string) => {
      if (uppy && !uppy.getPlugin('DropTarget')) {
        uppy.use(DropTarget, {
          target: `#${dropAreaId}`,
          onDrop: (e: MouseEvent) => {
            setError(null);
            setFailedFiles([]);
            e.preventDefault();
          },
        });
      }
    },
    [uppy, setError, setFailedFiles],
  );

  const removeDropArea = useCallback(() => {
    const plugin = uppy?.getPlugin('DropTarget');

    if (plugin) {
      uppy?.removePlugin(plugin);
    }
  }, [uppy]);

  const files = uppy?.getFiles() ?? [];

  const clearFiles = () => {
    files.forEach((file) => uppy?.removeFile(file.id));
  };

  const handleUploadRequestStateChange = useCallback(
    (isUploadInFlight: boolean) => {
      setIsUploadInFlight?.(isUploadInFlight);
    },
    [setIsUploadInFlight],
  );

  return {
    initialiseDropArea,
    handleFileSelect,
    handleFileRemove,
    error: context.error,
    files,
    removeDropArea,
    clearFiles,
    uppy,
    restrictions: uppy?.getState()?.opts?.restrictions as
      | UppyOptions['restrictions']
      | undefined,

    handleUploadErrors: setUploadErrorCount,
    hasUploadNetworkErrors: uploadErrorCount > 0,
    isUploadInFlight: isUploadInFlight,
    handleUploadRequestStateChange,
  };
};
